// This entire file is dreived from react-virtualized's WindowScroller
// See https://github.com/bvaughn/react-virtualized/blob/9.21.0/source/WindowScroller/WindowScroller.js
// We needed to rework it to deal with scaling issues.
// Internal dependencies are inlined whenever possible, with the exception of
//
import { getCurrentTimestampInMilliseconds } from "common/Support";
import * as React from "react";
import * as ReactDOM from "react-dom";
import type { DetectElementResize } from "./detect-element-resize";
import createDetectElementResize from "./detect-element-resize";

// From ../../utils/requestAnimationTimeout.js
export type AnimationTimeoutId = {
    id: number;
};

export const cancelAnimationTimeout = (frame: AnimationTimeoutId) => cancelAnimationFrame(frame.id);

/**
 * Recursively calls requestAnimationFrame until a specified delay has been met or exceeded.
 * When the delay time has been reached the function you're timing out will be called.
 *
 * Credit: Joe Lambert (https://gist.github.com/joelambert/1002116#file-requesttimeout-js)
 */
export const requestAnimationTimeout = (callback: () => void, delay: number): AnimationTimeoutId => {
    let start: number = 0;
    // wait for end of processing current event handler, because event handler may be long
    Promise.resolve()
        .then(() => {
            start = getCurrentTimestampInMilliseconds();
        })
        // This is not expected to ever fail.
        .catch(() => {
            /* And this has to be an explicit no-op to shut ESLint up. */
        });

    const timeout = () => {
        if (getCurrentTimestampInMilliseconds() - start >= delay) {
            callback();
        } else {
            frame.id = requestAnimationFrame(timeout);
        }
    };

    const frame: AnimationTimeoutId = {
        id: requestAnimationFrame(timeout),
    };

    return frame;
};

// From ./utils/onScroll.js in react-virtualized
let mountedInstances: WindowScroller[] = [];
let disablePointerEventsTimeoutId: AnimationTimeoutId | null = null;
let originalBodyPointerEvents: CSSStyleDeclaration["pointerEvents"] | null = null;

function enablePointerEventsIfDisabled() {
    if (disablePointerEventsTimeoutId !== null) {
        disablePointerEventsTimeoutId = null;

        if (originalBodyPointerEvents !== null) {
            document.body.style.pointerEvents = originalBodyPointerEvents;
        }

        originalBodyPointerEvents = null;
    }
}

function enablePointerEventsAfterDelayCallback() {
    enablePointerEventsIfDisabled();
    mountedInstances.forEach(instance => instance.__resetIsScrolling());
}

function onScrollWindow(event: Event) {
    enablePointerEventsAfterDelay();
    mountedInstances.forEach(instance => {
        if (instance.props.scrollElement === event.currentTarget) {
            instance.__handleWindowScrollEvent();
        }
    });
}

function enablePointerEventsAfterDelay() {
    if (disablePointerEventsTimeoutId !== null) {
        cancelAnimationTimeout(disablePointerEventsTimeoutId);
    }

    let maximumTimeout = 0;
    mountedInstances.forEach(instance => {
        maximumTimeout = Math.max(maximumTimeout, instance.props.scrollingResetTimeInterval);
    });

    disablePointerEventsTimeoutId = requestAnimationTimeout(enablePointerEventsAfterDelayCallback, maximumTimeout);
}

export function registerScrollListener(component: WindowScroller, element: Element | Window) {
    if (!mountedInstances.some(instance => instance.props.scrollElement === element)) {
        element.addEventListener("scroll", onScrollWindow);
    }
    mountedInstances.push(component);
}

export function unregisterScrollListener(component: WindowScroller, element: Element | Window) {
    mountedInstances = mountedInstances.filter(instance => instance !== component);
    if (mountedInstances.length === 0) {
        element.removeEventListener("scroll", onScrollWindow);
        if (disablePointerEventsTimeoutId !== null) {
            cancelAnimationTimeout(disablePointerEventsTimeoutId);
            enablePointerEventsIfDisabled();
        }
    }
}

// From ./utils/dimensions.js in react-virtualized
function getDimensions(element: Element | Window, scale: number) {
    if (element instanceof Element) {
        const { height, width } = element.getBoundingClientRect();
        return {
            height: height / scale,
            width: width / scale,
        };
    } else {
        const { innerHeight: height, innerWidth: width } = element;
        return {
            height: height / scale,
            width: width / scale,
        };
    }
}

function ensureElement(element: Element | Window) {
    if (element instanceof Window) {
        return document.documentElement;
    }
    return element;
}

function getScrollOffset(maybeElement: Element | Window) {
    const element = ensureElement(maybeElement);
    return {
        top: element.scrollTop,
        left: element.scrollLeft,
    };
}

function getBoundingBox(element: Element | Window, scale: number) {
    const { top, left } = ensureElement(element).getBoundingClientRect();
    return {
        top: top / scale,
        left: left / scale,
    };
}

function getPositionOffset(element: Element, container: Element | Window, scale: number) {
    const scrollOffset = getScrollOffset(container);
    const elementRect = getBoundingBox(element, scale);
    const containerRect = getBoundingBox(container, scale);
    return {
        top: elementRect.top + scrollOffset.top - containerRect.top,
        left: elementRect.left + scrollOffset.left - containerRect.left,
    };
}

export interface WindowScrollerChildProps {
    onChildScroll: (scrollPosition: { scrollTop: number }) => void;
    registerChild: (element: Element) => void;
    height: number;
    isScrolling: boolean;
    scrollLeft: number;
    scrollTop: number;
    width: number;
}

type Props = {
    /**
     * Function responsible for rendering children.
     * This function should implement the following signature:
     * ({ height, isScrolling, scrollLeft, scrollTop, width }) => PropTypes.element
     */
    children: (props: WindowScrollerChildProps) => React.ReactNode;

    /** Callback to be invoked on-resize: ({ height, width }) */
    onResize: (dimensions: { height: number; width: number }) => void;

    /** Callback to be invoked on-scroll: ({ scrollLeft, scrollTop }) */
    onScroll: (dimensions: { scrollLeft: number; scrollTop: number }) => void;

    /** Element to attach scroll event listeners. */
    scrollElement: Element | Window;
    /**
     * Wait this amount of time after the last scroll event before resetting child `pointer-events`.
     */
    scrollingResetTimeInterval: number;

    /** Height used for server-side rendering */
    serverHeight: number;

    /** Width used for server-side rendering */
    serverWidth: number;

    /** The scale factor used when rendering. Assumed to be 1 if missing. */
    scale?: number;
};

type State = {
    height: number;
    width: number;
    isScrolling: boolean;
    scrollLeft: number;
    scrollTop: number;
};

/**
 * Specifies the number of miliseconds during which to disable pointer events while a scroll is in progress.
 * This improves performance and makes scrolling smoother.
 */
export const IS_SCROLLING_TIMEOUT = 150;

export class WindowScroller extends React.PureComponent<Props, State> {
    static defaultProps = {
        onResize: () => {
            /* Do nothing by default */
        },
        onScroll: () => {
            /* Do nothing by default */
        },
        scrollingResetTimeInterval: IS_SCROLLING_TIMEOUT,
        scrollElement: window,
        serverHeight: 0,
        serverWidth: 0,
    };

    _isMounted = false;
    _positionFromTop = 0;
    _positionFromLeft = 0;
    _detectElementResize: DetectElementResize = createDetectElementResize();
    _child?: Element;

    constructor(props: Props) {
        super(props);
        this.state = {
            ...getDimensions(props.scrollElement, props.scale ?? 1),
            isScrolling: false,
            scrollLeft: 0,
            scrollTop: 0,
        };
    }

    public updatePosition() {
        const { scrollElement, onResize, scale } = this.props;
        const { height, width } = this.state;

        // eslint-disable-next-line react/no-find-dom-node
        const thisNode = this._child ?? ReactDOM.findDOMNode(this);
        if (thisNode instanceof Element) {
            const offset = getPositionOffset(thisNode, scrollElement, scale ?? 1);
            this._positionFromTop = offset.top;
            this._positionFromLeft = offset.left;
        }

        const dimensions = getDimensions(scrollElement, scale ?? 1);
        if (height !== dimensions.height || width !== dimensions.width) {
            this.setState({
                height: dimensions.height,
                width: dimensions.width,
            });
            onResize({
                height: dimensions.height,
                width: dimensions.width,
            });
        }
    }

    public componentDidMount() {
        const { scrollElement } = this.props;
        this.updatePosition();
        registerScrollListener(this, scrollElement);
        this._registerResizeListener(scrollElement);

        this._isMounted = true;
    }

    public componentDidUpdate(prevProps: Props) {
        const { scrollElement } = this.props;
        const { scrollElement: prevScrollElement } = prevProps;

        if (prevScrollElement !== scrollElement) {
            this.updatePosition();

            unregisterScrollListener(this, prevScrollElement);
            registerScrollListener(this, scrollElement);

            this._unregisterResizeListener(prevScrollElement);
            this._registerResizeListener(scrollElement);
        }
    }

    public componentWillUnmount() {
        const { scrollElement } = this.props;
        unregisterScrollListener(this, scrollElement);
        this._unregisterResizeListener(scrollElement);

        this._isMounted = false;
    }

    public render() {
        const { children } = this.props;
        const { isScrolling, scrollTop, scrollLeft, height, width } = this.state;

        return children({
            onChildScroll: this._onChildScroll,
            registerChild: this._registerChild,
            height,
            isScrolling,
            scrollLeft,
            scrollTop,
            width,
        });
    }

    private _registerChild = (element: Element) => {
        this._child = element;
        this.updatePosition();
    };

    private _onChildScroll = ({ scrollTop }: { scrollTop: number }) => {
        if (this.state.scrollTop === scrollTop) {
            return;
        }

        const { scrollElement } = this.props;
        if (scrollElement instanceof Window) {
            scrollElement.scrollTo(0, scrollTop + this._positionFromTop);
        } else {
            scrollElement.scrollTop = scrollTop + this._positionFromTop;
        }
    };

    private _registerResizeListener = (element: Element | Window) => {
        this._detectElementResize.addResizeListener(element, this._onResize);
    };

    private _unregisterResizeListener = (element: Element | Window) => {
        this._detectElementResize.removeResizeListener(element, this._onResize);
    };

    private _onResize = () => {
        this.updatePosition();
    };

    // Referenced by utils/onScroll
    public __handleWindowScrollEvent = () => {
        if (!this._isMounted) {
            return;
        }

        const { onScroll } = this.props;

        const scrollElement = ensureElement(this.props.scrollElement);

        // These scroll offsets are a bit subtle.
        // If you've scrolled down, this._positionFromTop is liable to go negative,
        // since it's "further up" the page than the initial 0 position.
        // this._positionFromTop also takes the scroll element's scrollTop into
        // account, but for our internal scrollTop, we don't want that.
        //
        // So not only do we remove scrollTop, we also negate this._positionFromTop
        // in one go. This gives us a positive scrollTop for scrolling down.
        // We don't care about negative scroll, that can be clamped to zero.
        const scrollLeft = Math.max(0, scrollElement.scrollLeft - this._positionFromLeft);
        const scrollTop = Math.max(0, scrollElement.scrollTop - this._positionFromTop);

        this.setState({
            isScrolling: true,
            scrollLeft,
            scrollTop,
        });

        onScroll({
            scrollLeft,
            scrollTop,
        });
    };

    // Referenced by utils/onScroll
    public __resetIsScrolling = () => {
        this.setState({
            isScrolling: false,
        });
    };
}
